import { MutableRefObject, useState } from 'react';
import { Col, Row } from 'react-bootstrap'
import { ScreenSize, useScreenSize } from '@/ui/hooks/useScreenSize';
import { translate } from '@/domain/utils/translate';
import { lowercaseCapitalValidation, numberandSymbolValidation } from '@/domain/genericValidations/usePasswordValidation';
import InputForm from '@/ui/components/forms/InputForm'


interface PasswordInputProps {
    passwordRef: MutableRefObject<HTMLInputElement | null>;
    email: string;
    passwordInputWithError: boolean;
    setPasswordInputWithError?: (passwordInputWithError: boolean) => void
    handleValidate?: () => void;
    isModal?: boolean;
}

const PasswordInput = ({
    passwordRef,
    email,
    passwordInputWithError,
    handleValidate = () => { },
    isModal = false,
    setPasswordInputWithError = () => { }
}: PasswordInputProps): JSX.Element => {

    /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido */
    const { width, maxLargeWidth }: ScreenSize = useScreenSize();
    /** Variable que define si es entorno mobile o desktop */
    const isMobile: boolean = width < maxLargeWidth;

    /** Maneja un state ocultar o mostrar el password digitado  */
    const [showPasswordText, setShowPasswordText] = useState<boolean>(false);
    /** Muestra y Oculta la contraseña cada vez que se declara  */
    const switchPasswordText = (): void => setShowPasswordText(!showPasswordText);

    /** Define el estado inicial del <li> de validación que el texto no contenga el correo */
    const [emailLi, setEmailLi] = useState<string>('');

    /** Define el estado inicial del <li> de validación de longitud de texto */
    const [lengthLi, setLengthLi] = useState<string>('');
    /** Define el estado inicial del <li> de validación de letras mayusculas y minusculas */
    const [minusMayusLi, setMinusMayusLi] = useState<string>('');
    /** Define el estado inicial del <li> de validación de numeros y textos */
    const [numberAndSymbolLi, setNumberAndSymbolLi] = useState<string>('');

    /** Llamado de Caso de uso que valida el <li> de Letras mayus y minus en la validación de contraseña */
    const validaMinusMayus: (password: string) => boolean = lowercaseCapitalValidation;
    /** Llamado de Caso de uso que valida el <li> de numeros y signos en la validación de contraseña */
    const validaNumberSymbol: (password: string) => boolean = numberandSymbolValidation;

    /** Función que válida el <li> de longitud en validación de contraseña*/
    const lengthLiValidation = (text: string): boolean => {
        if (text.length < 8 || text.length > 16) {
            setLengthLi('text-complementary-2');
            return true;
        } else {
            setLengthLi('text-complementary-1');
            return false;
        }
    };

    /** Función que válida el <li> de letras mayusculas y minusculas en validación de contraseña*/
    const minusMayusLiValidation = (text: string): boolean => {
        if (!validaMinusMayus(text)) {
            setMinusMayusLi('text-complementary-2');
            return true;
        } else {
            setMinusMayusLi('text-complementary-1');
            return false;
        }
    };

    /** Función que válida el <li> de numeros y signos en validación de contraseña*/
    const numberSymbolLiValidation = (text: string): boolean => {
        if (!validaNumberSymbol(text)) {
            setNumberAndSymbolLi('text-complementary-2');
            return true;
        } else {
            setNumberAndSymbolLi('text-complementary-1');
            return false;
        }
    };

    /** Función que válida el <li> para que no contenga el correo electrónico*/
    const emailLiValidation = (text: string = '', email: string = ''): boolean => {
        if (text.toUpperCase() === email.toUpperCase()) {
            setEmailLi('text-complementary-2');
            return true;
        } else {
            setEmailLi('text-complementary-1');
            return false;
        }
    };

    /** Función que válida la contraseña y que todos los <li> sean validos*/
    const passwordValidation = (password: string, email: string): void => {
        if (password.length < 1) {
            setLengthLi('');
            setMinusMayusLi('');
            setNumberAndSymbolLi('');
            setEmailLi('');
            setPasswordInputWithError(true);
        } else if (password.length > 0) {
            const lengthResponse = lengthLiValidation(password);
            const emailResponse = emailLiValidation(password, email);
            const caseResponse = minusMayusLiValidation(password);
            const formatResponse = numberSymbolLiValidation(password);

            if (lengthResponse || emailResponse || caseResponse || formatResponse) {
                setPasswordInputWithError(true);
            } else {
                setPasswordInputWithError(false);
            }
        }
    };

    /** Validaciones de contraseña */
    const handleValidatePassword = (): void => {
        passwordValidation(passwordRef.current?.value ?? '', email ?? '');
        handleValidate && handleValidate();
    }

    /**CONSTANTES DE TEXTO */
    const CLIENT_INFO_PASSWORD_LABEL = translate('session.password_label');
    const PASSWORD_RULES_LABEL = translate('session.password_rules_label');
    const LENGHT_RULE_LABEL = translate('session.length_rule_label');
    const WORDS_RULE_LABEL = translate('session.words_rule_label');
    const CHARACTERES_RULE_LABEL = translate('session.characters_rule_label');
    const SAME_EMAIL_PASSWORD_RULE_LABEL = translate('session.same_email_rule_label');

    return (
        <>
            <Row>
                <Col>
                    <InputForm
                        iconName={showPasswordText ? 'ico-show' : 'ico-hide'}
                        className={isMobile ? 'pt-2 mb-3' : 'mb-3'}
                        label={CLIENT_INFO_PASSWORD_LABEL}
                        classLabel='p-regular text-secondary-1'
                        inputRef={passwordRef}
                        type={showPasswordText ? 'text' : 'password'}
                        onChange={handleValidatePassword}
                        withErrors={passwordInputWithError}
                        lastIconClicked={switchPasswordText}
                        lastIconClass={`last-input-icon p-0   ${showPasswordText ? '' : 'disabled'}`}
                        lastIconName={showPasswordText ? 'ico-show' : 'ico-hide'}
                    />
                </Col>
            </Row>
            <Row className={isMobile ? '' : isModal ? 'mt-4' : 'mx-1 px-5'}>
                <Col>
                    <p className='p-smaller text-secondary-1 fw-bold'>{PASSWORD_RULES_LABEL}</p>
                    <ul className={isMobile ? 'p-small' : 'text-secondary-1 p-title'}>
                        <li className={`text-secondary-2 pb-2 ${lengthLi}`}>{LENGHT_RULE_LABEL}</li>
                        <li className={`text-secondary-2 ${minusMayusLi}`}>{WORDS_RULE_LABEL}</li>
                        <li className={`text-secondary-2 py-2 ${numberAndSymbolLi}`}>{CHARACTERES_RULE_LABEL}</li>
                        <li className={`text-secondary-2 ${emailLi}`}>{SAME_EMAIL_PASSWORD_RULE_LABEL}</li>
                    </ul>
                </Col>
            </Row>
        </>
    )
}

export default PasswordInput